import { useEffect, useMemo, useRef, useState } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameMonth,
  isToday,
  format,
} from 'date-fns';
import { it } from 'date-fns/locale';
import { useVisiteCalendar } from '@offertify/query';
import { Visita } from '../../types';
import { ChevronLeft, ChevronRight, Loader2, X, CalendarDays, Plus } from 'lucide-react';
import {
  statoConfig,
  clienteName,
  venditoreName,
  formatOra,
} from './attivitaConfig';
import { useActivityTypes, resolveActivityType } from '../../hooks/useActivityTypes';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
/** How many activities to preview inside a day cell before collapsing to "+N". */
const MAX_CHIPS = 3;

/** Local `yyyy-MM-dd` key (no timezone shift — the backend `data` is a plain date). */
function dayKey(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

interface Props {
  /** Filters mirrored from the toolbar; applied to the fetched window.
   *  Valori multipli serializzati CSV (es. "programmata,completata"). */
  dashboardOnly?: boolean;
  drilldown?: Pick<import('@offertify/api-client').GetCalendarParams, 'kpi' | 'reference_date' | 'exclude_cancelled' | 'cliente_id'>;
  windowFrom?: string;
  windowTo?: string;
  tipo?: string;
  origine?: 'app' | 'manuale';
  interne?: boolean;
  onEdit?: (visita: Visita) => void;
  stato?: string;
  userId?: string;
  /** First-of-month of the visible calendar (owned by the parent so the export can follow it). */
  month: Date;
  onMonthChange: (next: Date) => void;
  canCreate?: boolean;
  onCreate?: (date: string) => void;
}

export default function AttivitaCalendar({ dashboardOnly, drilldown, windowFrom, windowTo, tipo, origine, interne, onEdit, stato, userId, month, onMonthChange, canCreate = false, onCreate }: Props) {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  // While the day slide-over is open: move focus into it, close on Escape, and
  // restore focus to the triggering day cell on close (aria-modal contract).
  useEffect(() => {
    if (!selectedDay) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeBtnRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setSelectedDay(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [selectedDay]);

  // The visible grid spans whole weeks (Mon-Sun), so it usually shows a few
  // leading/trailing days from the adjacent months — we fetch that full window.
  const gridStart = useMemo(() => startOfWeek(startOfMonth(month), { weekStartsOn: 1 }), [month]);
  const gridEnd = useMemo(() => endOfWeek(endOfMonth(month), { weekStartsOn: 1 }), [month]);
  const days = useMemo(
    () => eachDayOfInterval({ start: gridStart, end: gridEnd }),
    [gridStart, gridEnd],
  );

  const { data, isFetching } = useVisiteCalendar({
    from_date: windowFrom && windowFrom > dayKey(gridStart) ? windowFrom : dayKey(gridStart),
    to_date: windowTo && windowTo < dayKey(gridEnd) ? windowTo : dayKey(gridEnd),
    ...drilldown,
    dashboard_only: dashboardOnly, tipo, origine, interne,
    stato: stato || undefined,
    user_id: userId || undefined,
  });

  // Tipi attività dall'anagrafica (contesto 'cliente': copre tutti i codici
  // selezionabili su visite — visita/telefonata/videochiamata/email/riunione/nota).
  const { data: activityTypes } = useActivityTypes('cliente');

  // Bucket the window by day and sort each day chronologically (untimed last).
  const byDay = useMemo(() => {
    const map = new Map<string, Visita[]>();
    for (const v of data || []) {
      const key = (v.data || '').slice(0, 10);
      if (!key) continue;
      const list = map.get(key);
      if (list) list.push(v);
      else map.set(key, [v]);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (a.ora || '99:99').localeCompare(b.ora || '99:99'));
    }
    return map;
  }, [data]);

  const selectedVisite = selectedDay ? byDay.get(selectedDay) || [] : [];
  const selectedLabel = selectedDay
    ? capitalize(format(new Date(`${selectedDay}T00:00:00`), 'EEEE d MMMM yyyy', { locale: it }))
    : '';

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* Month toolbar */}
      <div className="flex items-center justify-between gap-3 p-3 border-b border-gray-200 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onMonthChange(subMonths(month, 1))}
            className="p-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
            aria-label="Mese precedente"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="min-w-[10rem] text-center text-base font-semibold text-gray-900">
            {capitalize(format(month, 'LLLL yyyy', { locale: it }))}
          </div>
          <button
            onClick={() => onMonthChange(addMonths(month, 1))}
            className="p-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
            aria-label="Mese successivo"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => onMonthChange(startOfMonth(new Date()))}
            className="ml-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            Oggi
          </button>
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
        </div>
        {/* Legend */}
        <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
          {(['programmata', 'completata', 'annullata', 'rinviata'] as const).map((s) => {
            const cfg = statoConfig(s);
            return (
              <span key={s} className="inline-flex items-center gap-1">
                <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
                {cfg.label}
              </span>
            );
          })}
        </div>
      </div>

      {/* Weekday header + day grid share one horizontal-scroll track so the grid
          stays legible on narrow phones instead of squishing to unreadable columns. */}
      <div className="overflow-x-auto">
        <div className="grid grid-cols-7 min-w-[42rem] border-b border-gray-200 bg-gray-50">
          {WEEKDAYS.map((w) => (
            <div key={w} className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
              {w}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 min-w-[42rem]">
        {days.map((day) => {
          const key = dayKey(day);
          const list = byDay.get(key) || [];
          const inMonth = isSameMonth(day, month);
          const today = isToday(day);
          const extra = list.length - MAX_CHIPS;
          const hasItems = list.length > 0;
          return (
            <div
              key={key}
              className={[
                'min-h-[7rem] border-b border-r border-gray-100 p-1.5 text-left align-top flex flex-col gap-1',
                inMonth ? 'bg-white' : 'bg-gray-50/60',
              ].join(' ')}
            >
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setSelectedDay(key)}
                  aria-label={`${format(day, 'd MMMM', { locale: it })}, ${list.length} attività`}
                  className={[
                    'inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-medium hover:ring-2 hover:ring-blue-300',
                    today ? 'bg-blue-600 text-white' : inMonth ? 'text-gray-700' : 'text-gray-400',
                  ].join(' ')}
                >
                  {format(day, 'd')}
                </button>
                <div className="flex items-center gap-1">
                  {list.length > 0 && <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 rounded-full px-1.5 py-0.5">{list.length}</span>}
                  {canCreate && <button type="button" onClick={() => onCreate?.(key)} className="rounded p-1 text-blue-600 hover:bg-blue-100" aria-label={`Crea attività il ${format(day, 'd MMMM', { locale: it })}`}><Plus className="h-3.5 w-3.5" /></button>}
                </div>
              </div>

              <button type="button" onClick={() => setSelectedDay(key)} className={`flex flex-1 flex-col gap-0.5 overflow-hidden text-left ${hasItems ? 'cursor-pointer hover:bg-blue-50/50' : 'cursor-default'}`} aria-label={hasItems ? `Apri ${list.length} attività del ${format(day, 'd MMMM', { locale: it })}` : `Apri il ${format(day, 'd MMMM', { locale: it })}`}>
                {list.slice(0, MAX_CHIPS).map((v) => {
                  const cfg = statoConfig(v.stato);
                  return (
                    <div
                      key={v.id}
                      title={`${formatOra(v.ora)} ${clienteName(v)}${v.serie_id ? v.is_eccezione ? ' · Eccezione serie' : ' · Ricorrente' : ''}${v.origine === 'app' ? ' · App' : ''}`.trim()}
                      className={`flex items-center gap-1 border-l-2 ${cfg.bar} bg-gray-50 rounded-r px-1 py-0.5`}
                    >
                      {v.ora && (
                        <span className="text-[10px] text-gray-500 tabular-nums shrink-0">
                          {formatOra(v.ora)}
                        </span>
                      )}
                      <span className="text-[11px] text-gray-700 truncate">{clienteName(v)}{v.origine === 'app' && <span className="ml-1 text-blue-600"> · App</span>}</span>
                    </div>
                  );
                })}
                {extra > 0 && (
                  <span className="text-[11px] font-medium text-blue-600 px-1">
                    +{extra} {extra === 1 ? 'altra' : 'altre'}
                  </span>
                )}
              </button>
            </div>
          );
        })}
        </div>
      </div>

      {/* Day detail slide-over — the full, scrollable list for the picked day */}
      {selectedDay && (
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setSelectedDay(null)}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-md bg-white h-full shadow-xl flex flex-col">
            <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-200">
              <div>
                <div className="flex items-center gap-2 text-gray-900">
                  <CalendarDays className="h-4 w-4 text-blue-600" />
                  <span className="font-semibold">{selectedLabel}</span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  {selectedVisite.length} attività
                </p>
              </div>
              <button
                ref={closeBtnRef}
                onClick={() => setSelectedDay(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100"
                aria-label="Chiudi"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {canCreate && (
              <div className="px-4 pt-4">
                <button type="button" onClick={() => { const date = selectedDay; setSelectedDay(null); onCreate?.(date); }} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-medium text-white hover:bg-blue-700">
                  <Plus className="h-4 w-4" /> Nuova attività per questo giorno
                </button>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {!selectedVisite.length && <p className="py-8 text-center text-sm text-gray-500">Nessuna attività in programma.</p>}
              {selectedVisite.map((v) => {
                const tipo = resolveActivityType(v.tipo, activityTypes);
                const TipoIcon = tipo.icon;
                const st = statoConfig(v.stato);
                return (
                  <div
                    key={v.id}
                    className={`border border-gray-200 border-l-4 ${st.bar} rounded-lg p-3`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900 tabular-nums">
                        {v.ora ? formatOra(v.ora) : 'Orario non definito'}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}
                      >
                        {st.icon}
                        {st.label}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900">{clienteName(v)}{v.origine === 'app' && <span className="ml-1 text-blue-600"> · App</span>}</div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-500">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
                        style={{ backgroundColor: `${tipo.colore}1A`, color: tipo.colore }}
                      >
                        <TipoIcon className="h-3.5 w-3.5" />
                        {tipo.label}
                      </span>
                      <span>· {venditoreName(v)}</span>
                    </div>
                    {onEdit && <button type="button" onClick={() => onEdit(v)} className="mt-2 text-sm text-blue-600">Modifica attività</button>}
                    {v.esito && (
                      <p className="mt-2 text-sm text-gray-600">
                        <span className="font-medium text-gray-500">Esito: </span>
                        {v.esito}
                      </p>
                    )}
                    {v.note && (
                      <p className="mt-1 text-sm text-gray-500 whitespace-pre-line">{v.note}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
